import { cn } from '@/lib/utils'
import type { PurchaseOrder } from '@/types/parts'

// Deep links for the common carriers; any other carrier falls back to plain
// text so the number is still visible and copyable.
const TRACKING_URLS: Record<string, string> = {
  ups: 'https://www.ups.com/track?tracknum=',
  fedex: 'https://www.fedex.com/fedextrack/?trknbr=',
  usps: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
}

export function trackingURL(carrier: string, trackingNumber: string): string | null {
  if (!carrier || !trackingNumber) return null
  const base = TRACKING_URLS[carrier.trim().toLowerCase()]
  return base ? base + encodeURIComponent(trackingNumber) : null
}

// Overdue = a sent PO whose promised ETA is before today (received/canceled
// POs are settled either way).
export function isOverdue(po: Pick<PurchaseOrder, 'status' | 'expected_delivery'>): boolean {
  if (po.status !== 'sent' || !po.expected_delivery) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return new Date(po.expected_delivery) < today
}

export function POETA({
  po,
  className,
}: {
  po: Pick<PurchaseOrder, 'status' | 'expected_delivery'>
  className?: string
}) {
  if (!po.expected_delivery) {
    return <span className={cn('text-muted-foreground', className)}>—</span>
  }
  const overdue = isOverdue(po)
  return (
    <span className={cn(className, overdue && 'font-medium text-destructive')}>
      {new Date(po.expected_delivery).toLocaleDateString()}
      {overdue ? ' · overdue' : ''}
    </span>
  )
}

export function POTracking({
  po,
  className,
}: {
  po: Pick<PurchaseOrder, 'carrier' | 'tracking_number'>
  className?: string
}) {
  if (!po.tracking_number) {
    return <span className={cn('text-muted-foreground', className)}>—</span>
  }
  const url = trackingURL(po.carrier, po.tracking_number)
  return (
    <span className={cn('inline-flex items-baseline gap-1.5', className)}>
      {po.carrier && <span className="text-xs text-muted-foreground">{po.carrier}</span>}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-xs hover:underline"
        >
          {po.tracking_number}
        </a>
      ) : (
        <span className="font-mono text-xs">{po.tracking_number}</span>
      )}
    </span>
  )
}
